import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, Minus, Plus, ShoppingCart, ArrowLeftRight } from "lucide-react";
import { api, imageUrl, formatEUR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ProductCard from "../components/ProductCard";
import ProductQAWidget from "../components/ProductQAWidget";
import RecoStrip from "../components/RecoStrip";
import PageSEO from "../components/SEO";
import { useCart, useUI } from "../lib/cart-context";
import { useAuth } from "../lib/auth-context";
import { useShop, useShopHref, SHOP_META } from "../lib/shop-context";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [categories, setCategories] = useState([]);
  const { addItem } = useCart();
  const { setCartOpen } = useUI();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const shopCtx = useShop();
  const shop = shopCtx?.shop || "envio";
  const shopHref = useShopHref();

  useEffect(() => {
    setProduct(null);
    setQty(1);
    window.scrollTo({ top: 0 });
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setProduct(null));
    api.get("/categories", { params: { shop } }).then((r) => setCategories(r.data)).catch(() => {});
  }, [slug, shop]);

  useEffect(() => {
    if (!product) return;
    api
      .get("/products", { params: { limit: 5, shop } })
      .then((r) => {
        // pick 4 items from same category, excluding current
        const list = (r.data || []).filter((p) => p.id !== product.id).slice(0, 4);
        setRelated(list);
      })
      .catch(() => {});
  }, [product, shop]);

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="h-96 rounded-2xl bg-neutral-100 animate-pulse" />
      </div>
    );
  }

  // Phase 32 — a product may not exist in the current storefront (e.g. the
  // user landed on `/local/producto/aceite-oliva` but the product is only
  // stocked in the Envío shop). Show a friendly 404 with a link to the shop
  // that DOES have it, so we don't dead-end the visitor.
  const productShops = product.shops || ["envio"];
  if (!productShops.includes(shop)) {
    const otherShop = productShops[0];
    const otherMeta = otherShop ? SHOP_META[otherShop] : null;
    return (
      <div
        data-testid="product-page-wrong-shop"
        className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-center"
      >
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
          Este producto no está en {SHOP_META[shop].name}
        </h1>
        <p className="mt-3 text-sm text-neutral-500">
          «{product.name}» solo está disponible en Tienda {otherMeta?.name || "otra"}.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {otherMeta && (
            <Button
              asChild
              className="rounded-full bg-[#00953B] hover:bg-[#007a30]"
              data-testid="product-goto-other-shop"
            >
              <Link to={`${otherMeta.prefix}/producto/${product.slug}`}>
                Ver en Tienda {otherMeta.name}
              </Link>
            </Button>
          )}
          <Button
            asChild
            variant="outline"
            className="rounded-full"
            data-testid="product-back-to-home"
          >
            <Link to={shopHref("/")}>Volver</Link>
          </Button>
        </div>
      </div>
    );
  }

  const parentCat = categories.find((c) => c.id === product.category_id);
  const nutri = product.nutritional_info;
  // Phase 32 — show a hint when the same product also lives in the OTHER shop.
  const otherShopCode = productShops.find((s) => s !== shop) || null;
  const otherShop = otherShopCode ? SHOP_META[otherShopCode] : null;

  const doAdd = () => {
    // Phase 39b fix — guest add-to-cart supported via X-Session-Id header.
    addItem(product, qty);
    setCartOpen(true);
  };

  return (
    <div
      data-testid="product-page"
      className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10"
    >
      <PageSEO
        title={product.name}
        description={
          (product.description || "").slice(0, 155) ||
          `${product.name} de ${product.brand} — ${product.unit_label}. Compra online y envía a los campamentos saharauis con MercaGo.`
        }
        canonical={`https://mercago.one/envio/producto/${product.slug}`}
        ogType="product"
        ogImage={product.image_url && product.image_url.startsWith("http") ? product.image_url : "https://mercago.one/og-image.png"}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "description": product.description || `${product.name} de ${product.brand}`,
          "brand": { "@type": "Brand", "name": product.brand },
          "image": product.image_url && product.image_url.startsWith("http")
            ? product.image_url
            : `https://mercago.one${product.image_url || ""}`,
          "sku": product.id,
          "offers": {
            "@type": "Offer",
            "priceCurrency": "EUR",
            "price": product.price_eur,
            "availability": product.in_stock === false
              ? "https://schema.org/OutOfStock"
              : "https://schema.org/InStock",
            "url": `https://mercago.one/envio/producto/${product.slug}`,
          },
        }}
      />
      <nav className="text-sm text-neutral-500 mb-6 flex items-center gap-2 flex-wrap">
        <Link to={shopHref("/")} className="hover:underline">Inicio</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        {parentCat && (
          <>
            <Link to={shopHref(`/categoria/${parentCat.slug}`)} className="hover:underline">
              {parentCat.name}
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
          </>
        )}
        <span className="text-neutral-900 font-medium truncate max-w-[60%]">
          {product.name}
        </span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Image */}
        <div className="relative aspect-square rounded-3xl bg-neutral-50 border border-neutral-100 overflow-hidden grid place-items-center">
          {product.is_new && (
            <Badge className="absolute top-4 left-4 bg-[#00953B] hover:bg-[#00953B] text-white rounded-full px-3">
              Nuevo
            </Badge>
          )}
          <img
            src={imageUrl(product.image_url)}
            alt={product.name}
            className="w-full h-full object-contain p-8"
            data-testid="product-image"
          />
        </div>

        {/* Info */}
        <div>
          <div className="text-sm uppercase tracking-wider text-neutral-500">
            {product.brand}
          </div>
          <h1
            data-testid="product-name"
            className="mt-1 text-3xl sm:text-4xl font-black tracking-tight leading-tight"
          >
            {product.name}
          </h1>
          <div className="mt-6 flex items-baseline gap-3">
            <div
              data-testid="product-price"
              className="text-4xl font-black text-neutral-900"
            >
              {formatEUR(product.price_eur)}
            </div>
            <div className="text-sm text-neutral-500">
              {product.unit_label} · {product.price_per_unit}
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <div className="inline-flex items-center rounded-full border border-neutral-200 overflow-hidden">
              <button
                data-testid="pdp-qty-decrement"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="h-12 w-12 grid place-items-center hover:bg-neutral-50"
                aria-label="Disminuir"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span
                data-testid="pdp-qty"
                className="h-12 min-w-[3rem] px-3 grid place-items-center font-semibold"
              >
                {qty}
              </span>
              <button
                data-testid="pdp-qty-increment"
                onClick={() => setQty((q) => q + 1)}
                className="h-12 w-12 grid place-items-center hover:bg-neutral-50"
                aria-label="Aumentar"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button
              data-testid="pdp-add-to-cart-btn"
              onClick={doAdd}
              className="h-12 flex-1 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white font-semibold gap-2 text-base"
            >
              <ShoppingCart className="h-5 w-5" />
              Añadir al carro · {formatEUR(product.price_eur * qty)}
            </Button>
          </div>

          {/* Tabs: descripción, ingredientes, nutricional */}
          <Tabs defaultValue="desc" className="mt-10">
            <TabsList className="bg-neutral-100 rounded-full p-1">
              <TabsTrigger
                data-testid="tab-desc"
                value="desc"
                className="rounded-full data-[state=active]:bg-white"
              >
                Descripción
              </TabsTrigger>
              <TabsTrigger
                data-testid="tab-ingr"
                value="ingr"
                className="rounded-full data-[state=active]:bg-white"
              >
                Ingredientes
              </TabsTrigger>
              {nutri && (
                <TabsTrigger
                  data-testid="tab-nutri"
                  value="nutri"
                  className="rounded-full data-[state=active]:bg-white"
                >
                  Nutricional
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="desc" className="pt-4 text-neutral-700 text-sm leading-relaxed">
              {product.description || "Sin descripción disponible."}
            </TabsContent>
            <TabsContent value="ingr" className="pt-4 text-neutral-700 text-sm leading-relaxed">
              {product.ingredients || "Información de ingredientes no disponible."}
            </TabsContent>
            {nutri && (
              <TabsContent value="nutri" className="pt-4">
                <div className="text-xs text-neutral-500 mb-2">Valores por 100 g / 100 ml</div>
                <div
                  data-testid="nutritional-grid"
                  className="grid grid-cols-2 sm:grid-cols-4 gap-3"
                >
                  <NutriBox label="Energía" value={nutri.energia} unit="kcal" />
                  <NutriBox label="Grasas" value={nutri.grasas} unit="g" />
                  <NutriBox label="Hidratos" value={nutri.hidratos} unit="g" />
                  <NutriBox label="Proteínas" value={nutri.proteinas} unit="g" />
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {/* AI Q&A widget */}
      <ProductQAWidget productId={product.id} />

      {/* Personalized recommendations */}
      <RecoStrip
        context="product"
        productId={product.id}
        testid="reco-strip-product"
      />

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="text-2xl font-black tracking-tight mb-4">Productos relacionados</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function NutriBox({ label, value, unit }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3">
      <div className="text-xs text-neutral-500">{label}</div>
      <div className="mt-1 font-bold text-lg">
        {value != null ? `${value} ${unit}` : "—"}
      </div>
    </div>
  );
}
